import React from 'react';
import { Loader2 } from 'lucide-react';

// One loading language for both Ads Management sub-tabs (Inspections/Meta and
// Services/Google). Changing a date range used to swap the whole page for a spinner —
// or, on the Services tab, show nothing at all — so a 6-8s query read as "frozen".
// Now: a hairline sweep at the top of the viewport, a pill that names what is being
// fetched, and the previous numbers left in place but dimmed, so the layout never jumps
// and nobody mistakes a stale figure for the new window.

export const AdsProgressBar = ({ active }) => (
  <div
    className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px] overflow-hidden"
    style={{ opacity: active ? 1 : 0, transition: 'opacity 220ms ease' }}
    data-testid="ads-progress-bar"
    aria-hidden={!active}
  >
    {active && (
      <div
        className="h-full w-2/5 bg-gradient-to-r from-transparent via-sky-400 to-transparent"
        style={{
          animation: 'wd-bar-sweep 1.15s cubic-bezier(0.45, 0, 0.55, 1) infinite',
          boxShadow: '0 0 12px 1px rgba(56, 189, 248, 0.85)',
        }}
      />
    )}
  </div>
);

export const AdsUpdatingPill = ({ active, label = 'Updating…', sub }) => {
  if (!active) return null;
  return (
    <div
      className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2"
      data-testid="ads-updating-pill"
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-center gap-3 rounded-full border border-slate-700/60 bg-slate-900/92 px-5 py-3 text-white shadow-2xl backdrop-blur-md"
        style={{ animation: 'wd-pill-in 280ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-sky-300" />
        <span className="text-sm font-medium tracking-tight">{label}</span>
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
      </div>
    </div>
  );
};

const Bone = ({ className = '', style }) => (
  <div
    className={`rounded-md ${className}`}
    style={{
      backgroundImage:
        'linear-gradient(90deg, rgb(226 232 240) 25%, rgb(241 245 249) 50%, rgb(226 232 240) 75%)',
      backgroundSize: '200% 100%',
      animation: 'wd-shimmer 1.5s linear infinite',
      ...style,
    }}
  />
);

// First paint only — there is nothing on screen yet, so show the SHAPE of the page
// instead of a lone spinner on white.
export const AdsAnalyticsSkeleton = ({ label = 'Loading ads performance…' }) => (
  <div className="space-y-4" data-testid="ads-analytics-skeleton" aria-busy="true">
    <div className="rounded-xl border bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Bone className="h-4 w-24" />
        {[64, 84, 92, 104, 104, 104, 72].map((w, i) => (
          <Bone key={i} className="h-8" style={{ width: w }} />
        ))}
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-3">
      <Bone className="h-10 flex-1 min-w-[220px] max-w-md" />
      <Bone className="h-10 w-44" />
      <Bone className="h-10 w-40" />
      <Bone className="h-10 w-40" />
    </div>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-xl border bg-white p-5"
          style={{ animation: `wd-fade-up 420ms ease-out ${i * 70}ms both` }}
        >
          <Bone className="h-3 w-24" />
          <Bone className="mt-3 h-7 w-32" />
          <Bone className="mt-3 h-3 w-20" />
        </div>
      ))}
    </div>

    <div className="rounded-xl border bg-white p-5">
      <Bone className="h-4 w-40" />
      <div className="mt-5 space-y-3">
        {[100, 82, 61, 44, 30].map((w, i) => (
          <div key={i} className="flex items-center gap-3">
            <Bone className="h-3 w-28 shrink-0" />
            <Bone className="h-7" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
    </div>

    <div className="rounded-xl border bg-white p-5">
      <Bone className="h-4 w-52" />
      <div className="mt-4 space-y-2.5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Bone className="h-9 flex-1" />
            <Bone className="h-9 w-20" />
            <Bone className="h-9 w-20" />
            <Bone className="h-9 w-16" />
          </div>
        ))}
      </div>
    </div>

    <div className="flex items-center justify-center gap-2 pt-1 text-sm text-gray-500">
      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
      {label}
    </div>
  </div>
);
